import { useEffect, useRef, useState } from "react";

interface UsePullToRefreshOptions {
    onRefresh: () => Promise<void>;
    threshold?: number; // 触发刷新的下拉距离阈值
    maxDistance?: number; // 内容区最大下移距离
}

const SCROLL_TOP_TOLERANCE = 1;

function isScrollableVertically( element: HTMLElement ) {
    return element.clientHeight > 0
        && element.getClientRects().length > 0
        && element.scrollHeight - element.clientHeight > SCROLL_TOP_TOLERANCE;
}

function areScrollableDescendantsAtTop( root: HTMLElement ) {
    if ( root.scrollTop > SCROLL_TOP_TOLERANCE ) {
        return false;
    }

    const elements = root.querySelectorAll<HTMLElement>( "*" );

    for ( const element of elements ) {
        if ( isScrollableVertically( element ) && element.scrollTop > SCROLL_TOP_TOLERANCE ) {
            return false;
        }
    }

    return true;
}

export function usePullToRefresh( { onRefresh, threshold = 80, maxDistance = 20 }: UsePullToRefreshOptions ) {
    const [ distance, setDistance ] = useState( 0 );
    const [ isRefreshing, setIsRefreshing ] = useState( false );
    const startYRef = useRef( 0 );
    const lastYRef = useRef( 0 );
    const isScrollingRef = useRef( false );

    useEffect( () => {
        const scrollableContainer = document.querySelector( ".pull-to-refresh-container" ) as HTMLElement;
        if ( !scrollableContainer ) return;

        const onTouchStart = ( e: TouchEvent ) => {
            // 只在外层容器及其内部滚动内容都到顶部时才允许下拉
            if ( areScrollableDescendantsAtTop( scrollableContainer ) && !isRefreshing ) {
                startYRef.current = e.touches[0].clientY;
                lastYRef.current = startYRef.current;
                isScrollingRef.current = true;
            }
        };

        const onTouchMove = ( e: TouchEvent ) => {
            if ( !isScrollingRef.current || isRefreshing ) return;

            // 如果已经不在顶部了，停止下拉刷新
            if ( !areScrollableDescendantsAtTop( scrollableContainer ) ) {
                isScrollingRef.current = false;
                setDistance( 0 );
                return;
            }

            const currentY = e.touches[0].clientY;
            const diff = currentY - startYRef.current;

            // 只在下拉时更新，上拉时忽略
            if ( diff > 0 ) {
                // 下移距离受限于 maxDistance
                const normalizedDistance = Math.min( diff, threshold + 40 );
                setDistance( normalizedDistance );
                lastYRef.current = currentY;
            }
        };

        const onTouchEnd = async () => {
            if ( !isScrollingRef.current ) return;
            isScrollingRef.current = false;

            // 如果下拉超过阈值，触发刷新
            if ( distance >= threshold ) {
                setIsRefreshing( true );
                try {
                    await onRefresh();
                } finally {
                    setIsRefreshing( false );
                    setDistance( 0 );
                }
            } else {
                // 没有达到阈值，回弹
                setDistance( 0 );
            }
        };

        scrollableContainer.addEventListener( "touchstart", onTouchStart, { passive: true } );
        scrollableContainer.addEventListener( "touchmove", onTouchMove, { passive: true } );
        scrollableContainer.addEventListener( "touchend", onTouchEnd );

        return () => {
            scrollableContainer.removeEventListener( "touchstart", onTouchStart );
            scrollableContainer.removeEventListener( "touchmove", onTouchMove );
            scrollableContainer.removeEventListener( "touchend", onTouchEnd );
        };
    }, [ distance, isRefreshing, onRefresh, threshold, maxDistance ] );

    return {
        distance,
        isRefreshing,
        contentOffsetY: Math.min( distance * 0.25, maxDistance ), // 内容区只下移 1/4 的下拉距离，最多 maxDistance
    };
}
